import React, { FC } from 'react';

import { AutoPayVariant } from '../../../types';
import { NPC_FUNNEL_AUTOPAY_TWO_KEY } from 'src/commons/constants';
import { getOptimizelyVariation } from 'src/commons/utilities/feature-flags';
import { useAnnualFee } from 'src/commons/utilities/useAnnualFee';

import {
  FeatureColumnContainer,
  FeatureRowContainer,
  AutoPayFeatureText,
  CalendarFilledIcon,
  CalendarEmptyIcon,
  FeatureListColumnContainer,
  FeatureListRowContainer,
  PeaceIcon,
  ZeroFeeIcon,
  SmallZeroFeeIcon,
  SmallSmileyIcon,
  SmileyIcon,
} from './styles';

interface AutoPayFeatureListProps {
  /**
   * isConditional is true for the two step autopay flow
   */
  isConditional?: boolean;
}

interface PaymentAssuranceIconProps {
  variant: AutoPayVariant;
}

const PaymentAssuranceIcon: FC<PaymentAssuranceIconProps> = ({ variant }) => {
  if (variant === 'a') return <SmallSmileyIcon />;
  if (variant === 'b') return <SmileyIcon />;
  return <PeaceIcon />;
};

export const AutoPayFeatureList: FC<AutoPayFeatureListProps> = ({
  isConditional = false,
}) => {
  const annualFee = useAnnualFee();
  const autoPayConditionalFeature = getOptimizelyVariation(
    NPC_FUNNEL_AUTOPAY_TWO_KEY
  ).toLowerCase();

  const isVariantA = autoPayConditionalFeature === 'variant-a' && isConditional;
  // Variant B is the only option that is shown for the one step auto pay flow
  const isVariantB =
    autoPayConditionalFeature === 'variant-b' || !isConditional;
  const isControl = !isVariantA && !isVariantB;

  const AutoPayContainer = isVariantA
    ? FeatureListColumnContainer
    : FeatureListRowContainer;
  const FeatureContainer = isVariantA
    ? FeatureRowContainer
    : FeatureColumnContainer;

  const getWaiveFeeText = (): string => {
    if (isControl) {
      return `Waive your $${annualFee} annual fee for the first year so you pay $0²`;
    }
    return `Skip your first $${annualFee} annual fee²`;
  };

  const getDateText = (): string => {
    if (isVariantA) return 'Choose a due date that works for you';
    if (isVariantB) return 'Choose your due date';
    return 'Choose a due date and payment option that works best for you';
  };

  const getPaymentAssuranceText = (): string => {
    if (isControl) {
      return "Enjoy peace of mind knowing you'll never miss a payment";
    }
    return 'Never miss a payment';
  };

  const getVariantName = (): AutoPayVariant => {
    if (isVariantA) return 'a';
    if (isVariantB) return 'b';
    return 'control';
  };

  const waiveFeeText = getWaiveFeeText();
  const chooseDateText = getDateText();
  const paymentAssuranceText = getPaymentAssuranceText();
  const variantName = getVariantName();

  return (
    <AutoPayContainer>
      <FeatureContainer variant={variantName}>
        {isVariantA ? <SmallZeroFeeIcon /> : <ZeroFeeIcon />}
        <AutoPayFeatureText firstListItem variant={variantName}>
          {waiveFeeText}
        </AutoPayFeatureText>
      </FeatureContainer>

      <FeatureContainer variant={variantName}>
        {isVariantA ? <CalendarEmptyIcon /> : <CalendarFilledIcon />}
        <AutoPayFeatureText variant={variantName}>
          {chooseDateText}
        </AutoPayFeatureText>
      </FeatureContainer>

      <FeatureContainer lastListItem variant={variantName}>
        <PaymentAssuranceIcon variant={variantName} />
        <AutoPayFeatureText lastListItem variant={variantName}>
          {paymentAssuranceText}
        </AutoPayFeatureText>
      </FeatureContainer>
    </AutoPayContainer>
  );
};
